import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  InputBase,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import { useMediaQuery } from "@mui/material";
import CustomPagination from "./CustomPagination";
import CloseIcon from "@mui/icons-material/Close";

const Status = () => {
  const [currentTab, setCurrentTab] = useState("sent");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [tabIndex, setTabIndex] = useState(0); // To track the active tab
  const [searchQuery, setSearchQuery] = useState("");
  const isMobile = useMediaQuery("(max-width: 768px)");

  // State for Dialog modal
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  // Refs to hold tab elements for dynamic sizing
  const tabRefs = useRef([]);
  const [tabStyles, setTabStyles] = useState({ width: 0, left: 0 });

  // Update the sliding background when the tabIndex changes
  useEffect(() => {
    const currentTab = tabRefs.current[tabIndex];
    if (currentTab) {
      const { offsetWidth, offsetLeft, width } = currentTab;

      setTabStyles({
        width: offsetWidth,
        left: offsetLeft,
      });
    }
  }, [tabIndex, isMobile]);

  const handleTabChange = (newIndex) => {
    setTabIndex(newIndex);
    setCurrentTab(
      newIndex === 0 ? "sent" : newIndex === 1 ? "failed" : "responded"
    );
  };

  const emailStatusData = {
    sent: [
      {
        id: 1,
        recipient: "user1@example.com",
        subject: "Meeting Update",
        message: "Hello, the meeting has been rescheduled to 3 PM.",
      },
      {
        id: 2,
        recipient: "user2@example.com",
        subject: "Invoice Details",
        message: "Please find the attached invoice for your reference.",
      },
      {
        id: 3,
        recipient: "user3@example.com",
        subject: "Project Update",
        message: "The project is progressing as per schedule.",
      },
      {
        id: 4,
        recipient: "user4@example.com",
        subject: "Event Invitation",
        message: "You are invited to our annual event on March 10th.",
      },
      {
        id: 5,
        recipient: "user5@example.com",
        subject: "Support Request",
        message: "Your support ticket has been received and is being reviewed.",
      },
    ],
    failed: [
      {
        id: 7,
        recipient: "user7@example.com",
        subject: "Proposal Rejected",
        message: "Unfortunately, your proposal was not approved.",
      },
      {
        id: 8,
        recipient: "user8@example.com",
        subject: "Event Failure",
        message:
          "The scheduled event had to be canceled due to unforeseen circumstances.",
      },
      {
        id: 9,
        recipient: "user9@example.com",
        subject: "System Error",
        message: "There was a system error processing your request.",
      },
    ],
    responded: [
      {
        id: 10,
        recipient: "user10@example.com",
        subject: "Query Response",
        message:
          "Thank you for reaching out. Here is the response to your query.",
      },
      {
        id: 11,
        recipient: "user11@example.com",
        subject: "Request Answer",
        message: "We have reviewed your request and here is our response.",
      },
    ],
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filterEmails = (emails) => {
    if (!searchQuery) return emails;
    return emails.filter(
      (email) =>
        email.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        email.recipient.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const handleEmailClick = (email) => {
    setSelectedEmail(email);
    setOpenDialog(true); // Open dialog on email click
  };

  const renderEmails = (emails) => {
    const filteredEmails = filterEmails(emails);
    return filteredEmails.length > 0 ? (
      <List sx={{ padding: 0 }}>
        {filteredEmails.map((email) => (
          <ListItem
            key={email.id}
            sx={{
              borderBottom: "1px solid #e0e0e0",
              ":hover": {
                backgroundColor: "#f9f9f9",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              },
              transition: "background-color 0.3s, box-shadow 0.3s",
            }}
            onClick={() => handleEmailClick(email)} // Open dialog when email is clicked
          >
            <ListItemText
              primary={email.subject}
              secondary={`Recipient: ${email.recipient}`}
            />
          </ListItem>
        ))}
      </List>
    ) : (
      <Typography
        color="textSecondary"
        sx={{ textAlign: "center", fontStyle: "italic" }}
      >
        No emails match your search.
      </Typography>
    );
  };

  return (
    <div
      style={{
        display: "block",
        minHeight: "100vh",
        position: "relative",
        width: "100%",
        margin: "0 auto",
        paddingLeft: "20px",
        paddingRight: isMobile ? "15px" : "0px",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#eceff1",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          marginBottom: "20px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#eceff1",
            borderRadius: "4px",
            width: { xs: "120px", sm: "200px" },
            padding: "2px 10px",
            border: "1px solid #CFD8DC",
            justifyContent: "space-between",
            transition: "all 0.3s ease-in-out",
            "&:hover": {
              borderColor: "#1976d2",
              boxShadow: "0 4px 8px rgba(25, 118, 210, 0.2)",
            },
            "&:focus-within": {
              boxShadow: "0 4px 8px rgba(25, 118, 210, 0.2)",
              borderColor: "#1976d2",
            },
          }}
        >
          <InputBase
            sx={{
              flex: 1,
              color: "#212121",
              fontSize: "14px",
            }}
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          maxWidth: "1200px",
          width: { xs: "100%", sm: "90%", md: "100%" },
          padding: "30px",
          borderRadius: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#eceff1",
            width: isMobile ? "100%" : "fit-content", // Adjusted width to fit content dynamically
            maxWidth: "500px", // Prevents it from stretching too much
            borderRadius: "8px",
            padding: "10px", // Increased horizontal padding for balance
            margin: "0 auto 20px", // Centers it and keeps margin at the bottom
            position: "relative",
          }}
        >
          {/* Sliding background */}
          <div
            style={{
              // display: "flex",
              content: "''",
              position: "absolute",
              padding: 4,
              top: "10%",
              left: `${tabStyles.left}px`,
              width: `${tabStyles.width}px`,
              // minWidth: "50px",
              height: "80%",
              backgroundColor: "#453c72",
              borderRadius: "6px",
              transition: "left 0.3s ease, width 0.3s ease",
              zIndex: 1,
              flexShrink: 0,
            }}
          ></div>

          {/* Tabs */}
          <div
            style={{
              display: "flex",
              gap: isMobile ? 0 : "10px",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 2, // Ensure tabs are above the background
            }}
          >
            {["Sent", "Failed", "Responded"].map((tab, index) => (
              <div
                key={tab}
                ref={(el) => (tabRefs.current[index] = el)}
                onClick={() => handleTabChange(index)}
                style={{
                  cursor: "pointer",
                  // backgroundColor:
                  //   tabIndex === index ? "#453c72" : "transparent",
                  color: tabIndex === index ? "white" : "black",
                  zIndex: 2,
                  minWidth: "100px",
                  textAlign: "center",
                }}
              >
                {tab}
              </div>
            ))}
          </div>
        </div>

        {/* Render emails based on the active tab */}
        <div>{renderEmails(emailStatusData[currentTab])}</div>
      </Paper>

      <div
        style={{
          display: "flex",
          width: isMobile ? "100%" : "40%",
          justifyContent: "flex-end",
          alignItems: "center",
          marginLeft: "auto",
          marginTop: 0,
        }}
      >
        <CustomPagination
          count={filterEmails(emailStatusData[currentTab])?.length}
          rowsPerPage={rowsPerPage}
          page={page} // Use `page` instead of `currentPage`
          onPageChange={(event, newPage) => handleChangePage(event, newPage)} // Use `handleChangePage` here
          onRowsPerPageChange={handleChangeRowsPerPage} // Use `handleChangeRowsPerPage` here
        />
      </div>

      {/* Dialog Modal */}
      <Dialog open={openDialog} onClose={handleDialogClose}>
        {/* <DialogTitle>Email Details</DialogTitle> */}
        <DialogActions>
          <IconButton
            onClick={handleDialogClose}
            sx={{
              position: "absolute",
              top: "10px",
              right: "10px",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogActions>

        <DialogContent>
          {selectedEmail && (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <div style={{ height: "50px" }}>
                <Typography variant="h6">{selectedEmail.subject}</Typography>
              </div>
              <div
                style={{
                  height: "70px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body1">
                  <span style={{ fontWeight: 600 }}>MDQ</span>{" "}
                  {`<${selectedEmail.recipient}>`}
                </Typography>
                <span>Feb 6, 2025, 8:29 AM (1 day ago)</span>
              </div>
              <div style={{ minHeight: "300px" }}>
                <textarea
                  variant="body1"
                  value={selectedEmail.message}
                  // aria-expanded={false}
                  style={{ width: "100%", minHeight: "250px", resize: "none" }}
                />
              </div>
            </Box>
          )}
        </DialogContent>
        {/* <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Close
          </Button>
        </DialogActions> */}
      </Dialog>
    </div>
  );
};

export default Status;
